import { supabase } from '../supabase';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { reportError } from '@/lib/telemetry';
import { mapPollResponseSummary, type PollResultOption } from './pollResponses';
import type { Json } from '@/types/database';
import { toRpcIdentityParams } from '@/lib/auth/rpcIdentityParams';
import { acquireActiveRunPersistenceWrite } from '@/lib/auth/persistenceWriteBarrier';

export type Question = {
  question_id: number;
  stem: string;
  key: string;
  distractor_1: string;
  distractor_2: string;
  distractor_3: string;
  question_fact: string;
  generation_tag_id: number;
  primary_level2_tag_id?: number;
  breadcrumbs: Array<{
    slug: string;
    display_name: string;
    level: number;
  }> | null;
  source_url?: string;
};

export const getRandomQuestions = async ({
  limit = 3,
  user_id,
  is_anonymous,
  tag = null,
  partner_id = null,
}: {
  limit?: number;
  user_id: string;
  is_anonymous: boolean;
  tag?: string | null;
  partner_id?: string | null;
}): Promise<Question[]> => {
  try {
    const { data, error } = await supabase.rpc('get_random_questions', {
      p_limit: limit,
      ...toRpcIdentityParams({ userId: user_id, isAnonymous: is_anonymous }),
      p_tag: tag?.toLowerCase() ?? undefined,
      p_partner_id: partner_id ?? undefined,
    });

    if (error) throw error;

    // Map response to Question type
    // Breadcrumbs are now hydrated client-side, so we initialize as empty array
    // We cast to unknown first to avoid the explicit any error, as we know the shape matches
    const sanitizedData = (data || []).map((q: Omit<Question, 'breadcrumbs'>) => ({
      // Map RPC result properties to Question internal properties if needed.
      // Based on RPC definition: stem -> "stem", key -> "key"
      // get_random_questions returns (question_id, generation_tag_id, stem, key, distractor_1... )
      // So data keys will match new Question keys.
      ...(q as Question & { primary_level2_tag_id: number }),
      breadcrumbs: [],
    }));

    return sanitizedData;
  } catch (error) {
    console.error('Error fetching random questions:', error);
    throw error;
  }
};

export type DDAQuestion = Question & {
  difficulty_theta?: number;
  selection_type?: string;
};

export type PollOption = {
  id: string;
  label: string;
  sort_index: number;
};

export type QuizQuestion = Question & {
  question_index: number;
  quiz_title?: string;
  is_answered?: boolean;
  progress_status?: 'not_started' | 'in_progress' | 'completed';
  total_questions?: number;
  answered_questions?: number;
  guess_sequence?: number[];
  /** Unresolved server checkpoint; never overload with the resolved answer fact above. */
  item_progress_guess_sequence?: number[];
  xp_earned?: number;
  persistence_revision?: number;
  rating?: number | null;
  feedback?: number | null;
  feedback_text?: string | null;
  edition_item_id?: string;
  answer_order?: number[] | null;
  item_type?: 'trivia' | 'poll' | string;
  poll_definition_id?: string | null;
  poll_prompt?: string | null;
  poll_options?: PollOption[] | null;
  poll_response?: {
    selectedOptionId: string;
    distribution: PollResultOption[];
    optionId?: string;
    pollDefinitionId?: string;
    sessionId?: string | null;
    persistenceRevision?: number;
    xpEarned?: number;
  };
};

export type QuizBySlugRow = {
  /** Live/draft playable artifact id (display/runtime metadata). */
  id: string;
  /** Durable Quiz id for gameplay logging and analytics. */
  quiz_id: string;
  /** Live or draft Quiz Edition id. */
  edition_id: string;
  /** Canonical durable public slug. */
  slug: string;
  canonical_slug: string;
  /** True when the request matched quiz_slug_aliases. */
  is_alias: boolean;
  title: string;
  description: string | null;
  partner_id: string | null;
  created_by: string | null;
  num_questions: number;
  lifecycle: string;
  created_at: string;
  updated_at: string;
  scheduled_for: string | null;
  social_leaderboard_enabled?: boolean;
  /** Null for non-Library Quizzes; empty means the Atrivial Library's full category pool. */
  library_category_slugs: string[] | null;
};

function firstRpcRow<T>(data: T | T[] | null | undefined): T | null {
  if (data == null) {
    return null;
  }
  return Array.isArray(data) ? (data[0] ?? null) : data;
}

/** get_quiz_by_slug returns a SETOF row; read the first match without .maybeSingle(). */
export async function fetchQuizBySlug(
  slug: string,
  client?: SupabaseClient
): Promise<QuizBySlugRow | null> {
  const { data, error } = await (client ?? supabase).rpc('get_quiz_by_slug', { p_slug: slug });

  if (error) {
    throw error;
  }

  return firstRpcRow(data as QuizBySlugRow | QuizBySlugRow[] | null);
}

export async function getQuizIdBySlug(slug: string): Promise<string> {
  const quiz = await fetchQuizBySlug(slug);
  if (!quiz) {
    throw new Error('Quiz not found');
  }

  return quiz.id;
}

export async function getQuizQuestions(
  editionId: string | null,
  userId: string | null,
  isAnonymous: boolean,
  client?: SupabaseClient
): Promise<QuizQuestion[]> {
  if (!editionId) {
    return [];
  }

  // Public route/copy callers intentionally have no self identity. Self-scoped
  // reads use the shared formatter so authenticated requests omit p_user_id.
  const identityParams =
    userId === null
      ? { p_is_anonymous: isAnonymous }
      : toRpcIdentityParams({ userId, isAnonymous });

  const { data, error } = await (client ?? supabase).rpc('get_quiz_questions', {
    p_edition_id: editionId,
    ...identityParams,
  });

  if (error) {
    throw error;
  }

  const rows =
    (data as Array<Omit<QuizQuestion, 'poll_response'> & { poll_response?: Json | null }> | null) ??
    [];

  return rows.map<QuizQuestion>((question) => {
    const { poll_response: rawPollResponse, ...baseQuestion } = question;
    if (!rawPollResponse) return baseQuestion;

    const summary = mapPollResponseSummary(rawPollResponse);
    if (!summary.selectedOptionId) return baseQuestion;

    return {
      ...baseQuestion,
      poll_response: {
        selectedOptionId: summary.selectedOptionId,
        distribution: summary.distribution,
        ...(summary.optionId ? { optionId: summary.optionId } : {}),
        ...(summary.pollDefinitionId ? { pollDefinitionId: summary.pollDefinitionId } : {}),
        ...(summary.sessionId !== undefined ? { sessionId: summary.sessionId } : {}),
        ...(summary.persistenceRevision !== undefined
          ? { persistenceRevision: summary.persistenceRevision }
          : {}),
        ...(summary.xpEarned !== undefined ? { xpEarned: summary.xpEarned } : {}),
      },
    };
  });
}

/**
 * Fetch questions using DDA-based Smart Random selection (v2 with server-authority filtering).
 * Returns 10 questions: 7 Goldilocks (L1 coverage) + 3 Discovery (highest volatility).
 *
 * Server-side filtering handles:
 * - Hidden tags (read from user_hidden_tags)
 * - Answered questions (identity-scoped)
 * - Seen-in-run questions (run-scoped)
 */
export const getQuestionsDDA = async ({
  user_id,
  is_anonymous = false,
  limit = 10,
  tag = null,
  partner_id = null,
  run_context_key,
  session_id = null,
  game_mode = 'standard',
}: {
  user_id: string | null;
  is_anonymous?: boolean;
  limit?: number;
  tag?: string | null;
  partner_id?: string | null;
  run_context_key: string | null;
  session_id?: string | null;
  game_mode?: 'easy' | 'standard';
}): Promise<DDAQuestion[]> => {
  if (!user_id) {
    throw new Error('Cannot fetch DDA questions before user identity is resolved');
  }

  try {
    const identityParams = toRpcIdentityParams({ userId: user_id, isAnonymous: is_anonymous });

    const { data, error } = await supabase.rpc('get_questions_dda_v2', {
      ...identityParams,
      p_limit: limit,
      p_tag: tag?.toLowerCase() ?? undefined,
      p_partner_id: partner_id ?? undefined,
      p_run_context_key: run_context_key ?? undefined,
      p_session_id: session_id ?? undefined,
      p_game_mode: game_mode,
    });

    if (error) throw error;

    // Ensure breadcrumbs are always an array
    const sanitizedData = ((data || []) as unknown as DDAQuestion[]).map((question) => ({
      ...question,
      breadcrumbs: question.breadcrumbs || [],
    }));

    return sanitizedData;
  } catch (error) {
    console.error('Error fetching DDA questions:', error);
    throw error;
  }
};

export type LogAnswerResult = {
  success: boolean;
  newBadges: string[];
  answer_id?: number;
  previous_level: number;
  current_level: number;
  level_delta: number;
  levels_crossed: number;
};

function requireNumberField(result: Record<string, unknown>, field: string): number {
  const value = result[field];
  if (typeof value !== 'number') {
    throw new Error(`log answer response missing numeric field: ${field}`);
  }
  return value;
}

function mapLogAnswerResult(data: unknown): LogAnswerResult {
  const result = (data ?? {}) as Record<string, unknown>;
  return {
    success: (result.success as boolean) ?? true,
    answer_id: (result.answer_id as number) ?? undefined,
    newBadges: (result.new_badges as string[]) ?? [],
    previous_level: requireNumberField(result, 'previous_level'),
    current_level: requireNumberField(result, 'current_level'),
    level_delta: requireNumberField(result, 'level_delta'),
    levels_crossed: requireNumberField(result, 'levels_crossed'),
  };
}

export type LogAnswerParams = {
  question_id: number;
  guess_sequence: number[];
  // Common fields
  rating?: number;
  feedback?: number;
  feedback_text?: string;
  user_id?: string;
  session_id?: string;
  is_anonymous?: boolean;
  xp_earned?: number;
  current_streak?: number;
  log_answer_id?: number; // Optional ID for updating existing log
} & (
  { mode?: 'standard' } | { mode: 'dda'; attempt_number: number; game_mode: 'easy' | 'standard' }
);

export const logAnswer = async (params: LogAnswerParams): Promise<LogAnswerResult> => {
  const writeLease = acquireActiveRunPersistenceWrite();
  if (!writeLease) {
    throw new Error('Cannot log answer while user identity is resolving');
  }

  try {
    const identityParams = toRpcIdentityParams({
      userId: params.user_id ?? '',
      isAnonymous: params.is_anonymous ?? false,
    });

    if (params.mode === 'dda') {
      const { data, error } = await supabase.rpc('log_answer_with_dda', {
        p_question_id: params.question_id,
        p_guess_sequence: params.guess_sequence,
        p_attempt_number: params.attempt_number,
        p_game_mode: params.game_mode,
        p_rating: params.rating ?? undefined,
        p_feedback: params.feedback ?? undefined,
        p_feedback_text: params.feedback_text ?? undefined,
        ...identityParams,
        p_session_id: params.session_id,
        p_xp_earned: params.xp_earned ?? 0,
        p_current_streak: params.current_streak ?? 0,
        p_answer_id: params.log_answer_id ?? undefined,
      });

      if (error) {
        throw error;
      }

      return mapLogAnswerResult(data);
    }

    // Standard mode
    const { error, data } = await supabase.rpc('log_answer', {
      p_question_id: params.question_id,
      p_guess_sequence: params.guess_sequence,
      p_rating: params.rating ?? undefined,
      p_feedback: params.feedback ?? undefined,
      p_feedback_text: params.feedback_text ?? undefined,
      ...identityParams,
      p_session_id: params.session_id,
      p_xp_earned: params.xp_earned ?? 0,
      p_current_streak: params.current_streak ?? 0,
      p_answer_id: params.log_answer_id ?? undefined,
    });

    if (error) {
      throw error;
    }

    return mapLogAnswerResult(data);
  } catch (error) {
    reportError({
      error,
      source: 'questions.logAnswer',
      supabaseError:
        error &&
        typeof error === 'object' &&
        'code' in error &&
        typeof error.code === 'string' &&
        'message' in error &&
        typeof error.message === 'string'
          ? (error as PostgrestError)
          : undefined,
      context: { questionId: params.question_id },
    });
    console.error('Error logging answer:', error);
    throw error;
  } finally {
    writeLease.release();
  }
};

export const logImpression = async ({
  question_id,
  session_id,
  question_index,
  source = 'standard',
  game_mode = 'standard',
  user_id,
  is_anonymous = false,
  run_context_key,
}: {
  question_id: number;
  session_id?: string;
  question_index?: number;
  source?: string;
  game_mode?: string;
  user_id: string | null;
  is_anonymous?: boolean;
  run_context_key: string | null;
}): Promise<void> => {
  if (!user_id) {
    // Impressions are fire-and-forget. Skip unresolved identity without
    // creating an unhandled rejection or issuing an unowned RPC.
    return;
  }

  try {
    const writeLease = acquireActiveRunPersistenceWrite();
    if (!writeLease) return;

    const identityParams = toRpcIdentityParams({ userId: user_id, isAnonymous: is_anonymous });

    try {
      const { error } = await supabase.rpc('log_question_impression', {
        p_question_id: question_id,
        p_session_id: session_id,
        p_question_index: question_index ?? 0,
        p_source: source,
        p_game_mode: game_mode,
        ...identityParams,
        p_run_context_key: run_context_key ?? undefined,
      });
      if (error) {
        throw error;
      }
    } finally {
      writeLease.release();
    }
  } catch (error) {
    // Fire and forget - do not throw to avoid interrupting game flow
    console.error('Error logging impression:', error);
  }
};
